import * as React from "react";
import styled from "react-emotion";
import {Control, Field, Icon, Input, TextArea} from "bloomer";
import Card from "../../../elements/Card";
import {getQAResult} from "../../../api/models/QuestionAnswering";
import Button from "../../../elements/Button";
import getWordsCount from "../../../utils/WordsCount";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import Spacer from "../../../elements/Spacer";
import InformationCard from "../../../elements/InformationCard";
import SectionName from "../../../elements/SectionName";
import WordsLabel from "../../../elements/WordsLabel";
import InputWarning from "../../../elements/InputWarning";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import {INVALID_FIELD_STYLE} from "../../../utils/Constants";

const Container = styled("div")`
  margin-left: 10px;
  min-height: 200px;
`;

interface IQuestionAnsweringModelState {
    loading: boolean;
    contextInput: string;
    contextInputEmpty: boolean;
    questionInput: string;
    questionInputEmpty: boolean;
    result: string;
    contextWordsCount: number;
    questionWordsCount: number;
    showContextInputLimitWarning: boolean;
    showQuestionInputLimitWarning: boolean;
    errorMessage: string;
}

class QuestionAnsweringModel extends React.Component<{}, IQuestionAnsweringModelState> {
    public CONTEXT_WORDS_LIMIT = 1000;
    public QUESTION_WORDS_LIMIT = 50;

    constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            contextInput: "",
            contextInputEmpty: false,
            questionInput: "",
            questionInputEmpty: false,
            result: "",
            contextWordsCount: 0,
            questionWordsCount: 0,
            showContextInputLimitWarning: false,
            showQuestionInputLimitWarning: false,
            errorMessage: ""
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.QUESTION_ANSWERING);
    }

    public updateContextWordsCount = async () => {
        const contextWordsCount = getWordsCount(this.state.contextInput);
        this.setState({
            contextWordsCount,
            showContextInputLimitWarning: contextWordsCount > this.CONTEXT_WORDS_LIMIT
        });
    };

    public updateQuestionWordsCount = async () => {
        const questionWordsCount = getWordsCount(this.state.questionInput);
        this.setState({
            questionWordsCount,
            showQuestionInputLimitWarning: questionWordsCount > this.QUESTION_WORDS_LIMIT
        });
    };

    public handleContextInputChange = (e: any) => {
        this.setState({
            contextInput: e.target.value,
            contextInputEmpty: false,
            errorMessage: ""
        }, async () => {
            await this.updateContextWordsCount();
        });
    };

    public handleQuestionInputChange = (e: any) => {
        this.setState({
            questionInput: e.target.value,
            questionInputEmpty: false,
            errorMessage: ""
        }, async () => {
            await this.updateQuestionWordsCount();
        });
    };

    public handleQuestionInputKeyPress = async (e: any) => {
        const code = e.which || e.keyCode;
        if (code === 13) {
            await this.onSubmit();
        }
    }

    public onSubmit = async () => {
        if (!this.state.contextInput.trim() || !this.state.questionInput.trim() || this.state.loading
            || this.state.showContextInputLimitWarning || this.state.showQuestionInputLimitWarning
        ) {
            this.setState({result: ""});

            if (!this.state.contextInput.trim()) {
                this.setState({
                    contextInputEmpty: true
                });
            }

            if (!this.state.questionInput.trim()) {
                this.setState({
                    questionInputEmpty: true
                });
            }

            return;
        }

        this.setState({
            loading: true,
            result: "",
            errorMessage: ""
        });

        let errorOccurred = false;

        const response = await getQAResult(this.state.contextInput.trim(), this.state.questionInput.trim()).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({
                    loading: false
                });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Unable to get answer."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.data.result && response.data.result.answer) {
            this.setState({
                loading: false,
                result: response.data.result.answer
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Unable to get answer."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.QUESTION_ANSWERING);
    };

    public onSubmitHandler = async (e: any) => {
        e.target.blur();

        await this.onSubmit();
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            contextInput: "",
            contextInputEmpty: false,
            questionInput: "",
            questionInputEmpty: false,
            result: "",
            contextWordsCount: 0,
            questionWordsCount: 0,
            showContextInputLimitWarning: false,
            showQuestionInputLimitWarning: false,
            errorMessage: ""
        });
    };

    public render() {
        return <Container>
            <Card>
                <SectionName>Context</SectionName>
                <Field>
                    <Control>
                        <TextArea
                            style={(this.state.contextInputEmpty || this.state.showContextInputLimitWarning) ? INVALID_FIELD_STYLE : undefined}
                            onChange={this.handleContextInputChange}
                            onPaste={this.handleContextInputChange}
                            value={this.state.contextInput}
                            placeholder="Enter context text here"
                        />
                        <div>
                            {
                                this.state.contextInputEmpty && <InputWarning>Please input a context.</InputWarning>
                            }
                            {
                                this.state.showContextInputLimitWarning &&
                                <InputWarning>Input size limit has been exceeded. Please decrease the
                                    size.</InputWarning>
                            }
                            <WordsLabel>{this.state.contextWordsCount}&nbsp;{this.state.contextWordsCount > 1 ? 'words' : 'word'}</WordsLabel>
                        </div>
                    </Control>
                </Field>
                <Spacer/>
                <SectionName>Question</SectionName>
                <Field>
                    <Control>
                        <Input
                            style={(this.state.questionInputEmpty || this.state.showQuestionInputLimitWarning) ? INVALID_FIELD_STYLE : undefined}
                            onChange={this.handleQuestionInputChange}
                            onPaste={this.handleQuestionInputChange}
                            onKeyPress={this.handleQuestionInputKeyPress}
                            value={this.state.questionInput}
                            placeholder="Enter your question here"
                        />
                        <div>
                            {
                                this.state.questionInputEmpty ?
                                    <InputWarning>Please input a question.</InputWarning> : <></>
                            }
                            {
                                this.state.showQuestionInputLimitWarning ? <InputWarning>
                                    Input size limit has been exceeded. Please decrease the size.
                                </InputWarning> : <></>
                            }
                            <WordsLabel>{this.state.questionWordsCount}&nbsp;{this.state.questionWordsCount > 1 ? 'words' : 'word'}</WordsLabel>
                        </div>
                    </Control>
                </Field>
                <div>
                    <Button onClick={this.onSubmitHandler}>Submit</Button>
                    <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                </div>
                {
                    this.state.loading ? <div>
                        <Spacer/>
                        <span>Loading...</span>
                        <Icon className="fas fa-spinner fa-spin"/>
                    </div> : <></>
                }
                {
                    this.state.result && !this.state.loading ? <div>
                        <Spacer/>
                        <SectionName>Answer</SectionName>
                        <InformationCard>
                            <div>{this.state.result}</div>
                        </InformationCard>
                    </div> : <></>
                }
                {
                    this.state.errorMessage && !this.state.loading ? <React.Fragment>
                        <Spacer/>
                        <InformationCard>{this.state.errorMessage}</InformationCard>
                    </React.Fragment> : <></>
                }
            </Card>
        </Container>;
    }
}

export default QuestionAnsweringModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/QuestionAnswering/QuestionAnsweringModel.tsx